/* ============================================================================
   Portal ↔ design-system bridge.

   The primitives now come from `@acs/ui` (ADR-0001's `shared/ui` slot). This
   module keeps the portal's older, narrower prop shapes working — `tone="ok"`,
   `variant="ghost"`, `<EmptyState>` taking children — so feature code migrates
   page by page instead of in one sweep. Import `@acs/ui` directly in anything
   new; reach for these only where the legacy shape is still in play.
   ============================================================================ */
import type { ButtonHTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import {
  Badge as UiBadge,
  Button as UiButton,
  EmptyState as UiEmptyState,
  Modal as UiModal,
  type BadgeTone,
  type ButtonVariant,
} from '@acs/ui';

export { Card, Field } from '@acs/ui';

type LegacyVariant = 'primary' | 'ghost' | 'danger';

/** The portal's `ghost` is the design system's outlined `secondary`; the design
 *  system's borderless `ghost` reads as unclickable where we use it. */
const VARIANT: Record<LegacyVariant, ButtonVariant> = {
  primary: 'primary',
  ghost: 'secondary',
  danger: 'danger',
};

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: LegacyVariant;
  loading?: boolean;
};

export function Button({ variant = 'primary', ...rest }: ButtonProps) {
  return <UiButton variant={VARIANT[variant]} {...rest} />;
}

type LegacyTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info';

const TONE: Record<LegacyTone, BadgeTone> = {
  neutral: 'neutral',
  ok: 'success',
  warn: 'warning',
  bad: 'danger',
  info: 'info',
};

export function Badge({ children, tone = 'neutral' }: PropsWithChildren<{ tone?: LegacyTone }>) {
  return <UiBadge tone={TONE[tone]}>{children}</UiBadge>;
}

/** Children-only empty state. New code should use `@acs/ui`'s
 *  title/description/actions version, which reads far better. */
export function EmptyState({ children }: PropsWithChildren) {
  return <UiEmptyState borderless title={children} />;
}

export function Modal({
  title,
  onClose,
  footer,
  children,
}: PropsWithChildren<{ title?: ReactNode; onClose(): void; footer?: ReactNode }>) {
  return (
    <UiModal title={title} onClose={onClose} footer={footer}>
      {children}
    </UiModal>
  );
}
